import { useMemo, useState } from 'react';
import type { CampoConfig, Etapa } from '../../types';

/**
 * Lista de campos do fluxo para montar uma cotação: o que o fornecedor VÊ e o
 * que ele PREENCHE. Mora fora do modal porque a mesma escolha é feita em dois
 * lugares — no pedido, card a card, e na configuração da etapa, como padrão.
 *
 * Contexto pode vir de qualquer etapa (o viajante e o destino ficam lá na
 * solicitação). Resposta só da etapa de origem: é nos campos dela que o valor
 * do fornecedor vai ser gravado.
 */
interface SeletorCamposCotacaoProps {
  etapaAtual: Etapa;
  etapas: Etapa[];
  contexto: string[];
  resposta: string[];
  onChange: (contexto: string[], resposta: string[]) => void;
  /** Valores do card, quando existe um — mostra uma prévia do que ele vai ler. */
  valores?: Record<string, any>;
  disabled?: boolean;
}

/** Rótulo não guarda valor: não serve de contexto nem de resposta — mas serve
 *  de divisória. Numa etapa com "Cotação 01" da Decolar e "Cotação 01" de
 *  veículos, é ele que diz qual é qual. */
const ehRotulo = (campo: CampoConfig) => campo.tipo === 'texto_informativo';
const cotavel = (campo: CampoConfig) => !ehRotulo(campo);

const resumirValor = (valor: any): string => {
  if (valor === null || valor === undefined || valor === '') return '—';
  if (Array.isArray(valor)) {
    if (valor.length === 0) return '—';
    if (valor[0] && typeof valor[0] === 'object') return `${valor.length} arquivo(s)`;
    return valor.join(', ');
  }
  if (typeof valor === 'object') {
    const e = Object.entries(valor).filter(([, v]) => v !== '' && v !== null && v !== undefined);
    return e.length ? e.map(([k, v]) => `${k}: ${v}`).join(' · ') : '—';
  }
  return String(valor);
};

export default function SeletorCamposCotacao({
  etapaAtual, etapas, contexto, resposta, onChange, valores, disabled,
}: SeletorCamposCotacaoProps) {
  const [busca, setBusca] = useState('');

  // A etapa de origem primeiro: é a única que pode receber resposta.
  const secoes = useMemo(() => {
    const todas = (etapas && etapas.length ? etapas : [etapaAtual]).filter(Boolean);
    const atual = todas.find(e => String(e.id) === String(etapaAtual.id)) || etapaAtual;
    const outras = todas.filter(e => String(e.id) !== String(etapaAtual.id));
    return [atual, ...outras]
      .map(etapa => ({
        etapa,
        ehAtual: String(etapa.id) === String(etapaAtual.id),
        campos: etapa.campos || [],
      }))
      .filter(s => s.campos.some(cotavel));
  }, [etapas, etapaAtual]);

  const idsDaEtapa = useMemo(
    () => new Set((etapaAtual.campos || []).map(c => String(c.id))),
    [etapaAtual]
  );

  // Um campo é ou contexto ou resposta — nunca os dois, senão o fornecedor
  // veria o valor que ele mesmo deveria propor.
  const marcarContexto = (id: string, ligado: boolean) => {
    if (disabled) return;
    onChange(
      ligado ? [...new Set([...contexto, id])] : contexto.filter(c => c !== id),
      ligado ? resposta.filter(c => c !== id) : resposta
    );
  };
  const marcarResposta = (id: string, ligado: boolean) => {
    if (disabled) return;
    onChange(
      ligado ? contexto.filter(c => c !== id) : contexto,
      ligado ? [...new Set([...resposta, id])] : resposta.filter(c => c !== id)
    );
  };

  /** "Vê" em bloco: numa etapa de 15 campos, marcar um a um é castigo. */
  const alternarSecao = (campos: CampoConfig[]) => {
    if (disabled) return;
    const ids = campos.filter(cotavel).map(c => String(c.id)).filter(id => !resposta.includes(id));
    const todosMarcados = ids.length > 0 && ids.every(id => contexto.includes(id));
    onChange(
      todosMarcados
        ? contexto.filter(id => !ids.includes(id))
        : [...new Set([...contexto, ...ids])],
      resposta
    );
  };

  const termo = busca.trim().toLowerCase();
  const secoesVisiveis = secoes
    .map(sec => ({
      ...sec,
      campos: termo
        ? sec.campos.filter(c => cotavel(c) && (c.nome || '').toLowerCase().includes(termo))
        : sec.campos,
    }))
    .filter(sec => sec.campos.some(cotavel));

  return (
    <div>
      {secoes.length > 1 && (
        <input
          value={busca}
          onChange={e => setBusca(e.target.value)}
          placeholder="Procurar campo…"
          className="w-full mb-2 px-3 py-1.5 text-[12px] border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
        />
      )}

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-[1fr_70px_80px] gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
          <span>Campo</span><span className="text-center">Vê</span><span className="text-center">Preenche</span>
        </div>

        {secoesVisiveis.length === 0 && (
          <div className="px-3 py-4 text-[12px] text-gray-400 text-center">
            Nenhum campo com esse nome.
          </div>
        )}

        {secoesVisiveis.map(sec => (
          <div key={String(sec.etapa.id)}>
            <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-gray-50/80 border-b border-gray-200">
              <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide truncate">
                {sec.etapa.nome}
                {sec.ehAtual && <span className="ml-1.5 text-emerald-600 normal-case">· etapa da cotação</span>}
              </span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => alternarSecao(sec.campos)}
                  className="text-[10px] text-blue-600 hover:text-blue-800 whitespace-nowrap"
                >
                  marcar/desmarcar "vê"
                </button>
              )}
            </div>

            {sec.campos.map(campo => {
              const id = String(campo.id);
              if (ehRotulo(campo)) {
                return (
                  <div
                    key={id}
                    className="px-3 py-1.5 border-b border-gray-100 bg-white text-[10px] font-medium text-gray-400 uppercase tracking-wide truncate"
                  >
                    {campo.nome}
                  </div>
                );
              }
              const ehContexto = contexto.includes(id);
              const ehResposta = resposta.includes(id);
              const podeResponder = idsDaEtapa.has(id);
              return (
                <div key={id} className="grid grid-cols-[1fr_70px_80px] gap-2 px-3 py-2 border-b border-gray-100 last:border-0 items-center hover:bg-gray-50/60">
                  <div className="min-w-0">
                    <div className="text-[12px] text-gray-800 truncate">{campo.nome}</div>
                    {ehContexto && valores && (
                      <div className="text-[10px] text-gray-400 truncate">{resumirValor(valores[id])}</div>
                    )}
                  </div>
                  <div className="text-center">
                    <input
                      type="checkbox"
                      checked={ehContexto}
                      disabled={disabled}
                      onChange={e => marcarContexto(id, e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>
                  <div className="text-center">
                    {podeResponder ? (
                      <input
                        type="checkbox"
                        checked={ehResposta}
                        disabled={disabled}
                        onChange={e => marcarResposta(id, e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                    ) : (
                      <span
                        className="text-[11px] text-gray-300"
                        title={`A resposta é gravada nos campos de ${etapaAtual.nome}. Campo de outra etapa só entra como contexto.`}
                      >—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
